import styled from 'styled-components';
import { Card } from 'antd';
import { COLORS } from '../../../../../../../constants/colors';

const getShadowStyle = (theme) => {
  if (!theme?.shadow_color) return '0 4px 8px rgba(0, 0, 0, 0.1)';

  const hexToRgba = (hex, alpha = 1) => {
    const [r, g, b] = hex.match(/\w\w/g).map(x => parseInt(x, 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const softShadowColor = hexToRgba(theme.shadow_color, 0.1);
  const hardShadowColor = hexToRgba(theme.shadow_color, 0.5);

  switch (theme?.card_style) {
    case 'soft-shadow-squared':
    case 'soft-shadow-slightly-rounded':
      return `0 6px 12px ${softShadowColor}`;
    case 'hard-shadow-squared':
    case 'hard-shadow-slightly-rounded':
      return `3px 3px 0 ${hardShadowColor}`;
    default:
      return `0 4px 8px ${softShadowColor}`;
  }
};

export const PreviewContainer = styled.div`
  padding: 0px;
  padding-bottom: 100px;
  border-radius: 25px;
  overflow-y: auto;
  height: 97vh;
  width: 100%;
  background-color: ${({ theme }) => theme?.background_color || '#f5f5f5'};
`;

export const ProfileHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  margin-top: 20px;
  text-align: center;
  background-color: ${({ theme }) => theme?.card_color || 'initial'};

  h2 {
    margin-top: 10px;
    font-size: 24px;
    color: ${({ theme }) => theme?.card_text_color || 'white'};
  }

  p {
    margin-top: 10px;
    font-size: 16px;
    color: ${({ theme }) => theme?.card_text_color || 'white'};
  }
`;

export const ProductCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 20px;
  margin-bottom: 20px;
  margin-left: auto;
  margin-right: auto;
  background-color: ${({ theme }) =>
    theme?.card_style?.includes('outline') ? 'transparent' : theme?.card_color || '#1c1c1c'};
  box-shadow: ${({ theme }) => getShadowStyle(theme)};
  border-radius: ${({ theme }) =>
    theme?.card_style?.includes('slightly-rounded') ? '10px' :
    theme?.card_style?.includes('squared') ? '0' : '15px'};
  border: ${({ theme }) =>
    theme?.card_style?.includes('outline') ? `2px solid ${theme?.card_color || '#000'}` : 'none'};
  text-align: center;

  .product-content {
    display: flex;
    width: 100%;
    align-items: flex-start;
    margin-bottom: 15px;
  }

  .product-info {
    flex-grow: 1;
    padding-left: 20px;
    text-align: left;
  }

  .product-image {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 90px;
    height: 90px;
    border-radius: 10px;
  }

  h3 {
    margin: 0;
    font-size: 18px;
    color: ${({ theme }) => theme?.card_text_color || 'white'};
  }

  p {
    margin: 5px 0;
    color: ${({ theme }) => theme?.card_text_color || '#d3d3d3'};
  }

  .product-price {
    font-size: 16px;
    color: ${({ theme }) => theme?.card_text_color || '#000'};

    .amount {
      font-weight: bold;
    }
  }

  .product-button {
    margin-top: auto;
    width: 100%;
    font-weight: bold;
    min-height: 55px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 20px;
    text-decoration: none;
    
    border-radius: ${({ theme }) => {
      if (theme?.button_style?.includes('completely-rounded')) return '27.5px';
      if (theme?.button_style?.includes('slightly-rounded')) return '10px';
      return '0';
    }};

    background-color: ${({ theme }) => 
      theme?.button_style?.includes('outline') ? 'transparent' : theme?.button_color || COLORS.$mainBlue} !important;

    border: ${({ theme }) => 
      theme?.button_style?.includes('outline') ? `2px solid ${theme?.card_color || COLORS.$mainBlue}` : 'none'} !important;

    color: ${({ theme }) => 
      theme?.button_style?.includes('outline') ? theme?.button_color || COLORS.$mainBlue : theme?.button_text_color || '#FFFFFF'} !important;

    transition: all 0.3s ease;

    .button-text {
      flex-grow: 1;
      text-align: left;
      color: inherit;
      transition: color 0.3s ease;
    }

    .button-icon {
      margin-left: 10px;
      display: flex;
      align-items: center;
      transition: color 0.3s ease;

      svg {
        path {
          transition: stroke 0.3s ease, fill 0.3s ease;
          stroke: currentColor;
          fill: currentColor;
        }
      }
    }

    &:hover {
      background-color: ${({ theme }) => 
        theme?.button_style?.includes('outline') 
          ? theme?.button_color || COLORS.$mainBlue
          : `${theme?.button_text_color || COLORS.$mainBlue}dd`} !important;
      
      .button-text {
        color: ${({ theme }) => 
          theme?.button_style?.includes('outline') 
            ? 'transparent'
            : theme?.button_text_color || '#FFFFFF'} !important;
      }

      .button-icon svg path {
        stroke: ${({ theme }) => 
          theme?.button_style?.includes('outline') 
            ? 'transparent'
            : theme?.button_text_color || '#FFFFFF'};
        fill: ${({ theme }) => 
          theme?.button_style?.includes('outline') 
            ? 'transparent'
            : theme?.button_text_color || '#FFFFFF'};
      }
    }
  }

  .ant-card-body {
    padding: 0px;
    width: 100%;
    border-radius: 15px;
  }
`;

export const EmailFormCard = styled(ProductCard)`
  .product-content {
    display: flex;
    width: 100%;
    align-items: flex-start;
    margin-bottom: 15px;
  }

  .product-info {
    flex-grow: 1;
    padding-left: 20px;
    text-align: left;
  }

  .product-image {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100px;
    height: 100px;
    border-radius: 10px;
  }

  h3 {
    margin: 0;
    font-size: 18px;
    color: ${({ theme }) => theme?.card_text_color || 'white'};
  }

  p {
    margin: 5px 0;
    color: ${({ theme }) => theme?.card_text_color || '#d3d3d3'};
  }

  .ant-form-item {
    margin-bottom: 16px;
  }

  .ant-input {
    height: 48px;
    border-radius: 8px;
  }

  .ant-btn-primary {
    width: 100%;
    min-height: 55px;
    padding: 0 20px;
    font-weight: bold;
    display: flex;
    justify-content: center;
    align-items: center;
    
    border-radius: ${({ theme }) => {
      if (theme?.button_style?.includes('completely-rounded')) return '27.5px';
      if (theme?.button_style?.includes('slightly-rounded')) return '10px';
      return '0';
    }};

    background-color: ${({ theme }) => 
      theme?.button_style?.includes('outline') ? 'transparent' : theme?.button_color || COLORS.$mainBlue} !important;

    border: ${({ theme }) => 
      theme?.button_style?.includes('outline') ? `2px solid ${theme?.card_color || COLORS.$mainBlue}` : 'none'} !important;

    color: ${({ theme }) => 
      theme?.button_style?.includes('outline') ? theme?.button_color || COLORS.$mainBlue : theme?.button_text_color || '#FFFFFF'} !important;

    transition: all 0.3s ease;

    .button-text {
      flex-grow: 1;
      text-align: left;
      color: inherit;
      transition: color 0.3s ease;
    }

    .button-icon {
      margin-left: 10px;
      display: flex;
      align-items: center;
      transition: color 0.3s ease;

      svg {
        path {
          transition: stroke 0.3s ease, fill 0.3s ease;
          stroke: currentColor;
          fill: currentColor;
        }
      }
    }

    &:hover {
      background-color: ${({ theme }) => 
        theme?.button_style?.includes('outline') 
          ? theme?.button_color || COLORS.$mainBlue
          : `${theme?.button_color || COLORS.$mainBlue}dd`} !important;
      
      .button-text {
        color: ${({ theme }) => 
          theme?.button_style?.includes('outline') 
            ? 'transparent'
            : theme?.button_text_color || '#FFFFFF'} !important;
      }

      .button-icon svg path {
        stroke: ${({ theme }) => 
          theme?.button_style?.includes('outline') 
            ? 'transparent'
            : theme?.button_text_color || '#FFFFFF'};
        fill: ${({ theme }) => 
          theme?.button_style?.includes('outline') 
            ? 'transparent'
            : theme?.button_text_color || '#FFFFFF'};
      }
    }

    &:focus, &:active {
      background-color: ${({ theme }) => 
        theme?.button_style?.includes('outline') ? 'transparent' : theme?.button_color || COLORS.$mainBlue} !important;
      color: ${({ theme }) => 
        theme?.button_style?.includes('outline') ? theme?.button_color || COLORS.$mainBlue : theme?.button_text_color || '#FFFFFF'} !important;
      border-color: ${({ theme }) => theme?.button_color || COLORS.$mainBlue} !important;

      .button-icon svg path {
        stroke: currentColor;
        fill: currentColor;
      }
    }
  }
`;